/* so-tracking — delivery tracking: browser events → so-mail POST /delivery/event, Clarity tags, and the
   hidden `_so_delivery` cart attribute. Task 28 (Charl 2026-09-28 "Add all to go-live").
   Contract: .superpowers/sdd/2026-09-25-postcode-delivery-rebuild/tracking-contract.md — sections B (events),
   C (cart attribute), D (Clarity). A and E are so-mail-side (Task 29).
   Privacy (contract, binding): never send coordinates, suburb text or account data; search_no_hits sends only
   the query LENGTH, never the text; the postcode itself never reaches Clarity.
   Pure helpers (deliveryAttrValue, buildEventPayload, dedupeCheck, quoteFailCode) are exported for node tests
   (scripts/tests/so-tracking.test.cjs) exactly like so-postcode.js. The browser glue below (send/clarity/the
   attribute writer) never throws and never blocks the UI: every call site is fire-and-forget. */
(function () {
  'use strict';
  var NODE = typeof module === 'object' && module.exports && typeof require === 'function';

  // ---- C. hidden cart attribute `_so_delivery` --------------------------------------------------
  // parts = {src ('typed'|'search'|'location'|'account'), pc (4-digit string or falsy to clear), head (the
  // headline option's short name), price (headline net price, rands), basis (cart basis, rands)}.
  // No postcode (cleared) → '' (contract C: "Cleared (set to \"\") if the customer clears the postcode.").
  function deliveryAttrValue(parts) {
    if (!parts || !parts.pc) return '';
    var src = parts.src === 'search' || parts.src === 'location' || parts.src === 'account' ? parts.src : 'typed';
    var price = Math.max(0, Math.round(Number(parts.price) || 0));
    var basis = Math.max(0, Math.round(Number(parts.basis) || 0));
    var head = String(parts.head == null ? '' : parts.head).replace(/[\r\n;']/g, ' ').replace(/\s+/g, ' ').trim();
    return 'v2;src=' + src + ';pc=' + parts.pc + ';head=' + head + ';price=' + price + ';free=' + (price === 0 ? 'y' : 'n') + ';basis=' + basis;
  }

  // ---- B. browser events → POST /delivery/event ---------------------------------------------------
  // Closed type list (contract B); server drops anything else, and so do we, before it's ever sent.
  var EVENT_TYPES = { sheet_open: 1, postcode_set: 1, locate_tap: 1, locate_fail: 1, locate_ok: 1,
    search_no_hits: 1, suggestion_pick: 1, quote_fail: 1, quote_ok: 1, signin_tap: 1, account_address_pick: 1 };
  var PAGES = { pdp: 1, cart: 1, drawer: 1, home: 1, collection: 1, other: 1 };

  // Builds the closed-shape body {t, pc, src, code, ms, pg, v}; null for an unknown type (never sent).
  function buildEventPayload(type, fields) {
    if (!EVENT_TYPES[type]) return null;
    fields = fields || {};
    var pg = PAGES[fields.pg] ? fields.pg : 'other';
    var pc = typeof fields.pc === 'string' && /^\d{4}$/.test(fields.pc) ? fields.pc : null;
    var src = fields.src === 'typed' || fields.src === 'search' || fields.src === 'location' || fields.src === 'account' ? fields.src : null;
    var code = fields.code == null ? null : String(fields.code).slice(0, 32);
    var ms = typeof fields.ms === 'number' && fields.ms >= 0 && isFinite(fields.ms) ? Math.round(fields.ms) : null;
    // search_no_hits (contract B): never the postcode, never the query text — code is the query LENGTH only.
    if (type === 'search_no_hits') { pc = null; code = fields.queryLength != null ? String(fields.queryLength) : null; }
    return { t: type, pc: pc, src: src, code: code, ms: ms, pg: pg, v: '2' };
  }

  // Dedupe: the same (t, pc, code) at most once per 10 s per page (contract B). Pure: the caller keeps
  // `history` between calls; returns {allow, history} — `history` is pruned of anything older than the window.
  var DEDUPE_MS = 10000;
  function dedupeKey(t, pc, code) { return t + '|' + (pc || '') + '|' + (code == null ? '' : code); }
  function dedupeCheck(history, t, pc, code, nowMs) {
    var key = dedupeKey(t, pc, code);
    var kept = (history || []).filter(function (e) { return nowMs - e.ts < DEDUPE_MS; });
    var seen = kept.some(function (e) { return e.key === key; });
    if (!seen) kept.push({ key: key, ts: nowMs });
    return { allow: !seen, history: kept };
  }

  // A failed /delivery or /delivery?lat&lng fetch → the quote_fail code (contract B's closed list:
  // 'timeout'|'network'|'http_<status>'|'empty'|'unconfirmed'). Pure: takes the Error fetchTimeout()/the
  // location lookup throws.
  function quoteFailCode(e) {
    var msg = (e && e.message) || '';
    if (e && e.name === 'AbortError') return 'timeout';
    if (/^HTTP \d+/.test(msg)) return 'http_' + msg.slice(5);
    if (msg === 'outside' || msg === 'bad quote') return 'empty';
    return 'network';
  }
  // A geolocation PositionError.code (1/2/3, or none) → contract B's locate_fail code.
  function locateFailCode(code) {
    if (code === 1) return 'denied';
    if (code === 2) return 'unavailable';
    if (code === 3) return 'timeout';
    return 'unsupported';
  }

  // ---- F. GA4 mirror (analytics/TRACKING-PLAN.md) -------------------------------------------------
  // The seven events the GA pixel subscribes to, republished as Shopify.analytics.publish('so:<type>').
  // Built from the already-sanitised /delivery/event body, minus the postcode: GA never gets `pc`, and
  // search_no_hits carries only the query length (`len`). The pixel whitelists these param names.
  var GA_EVENTS = { sheet_open: 1, postcode_set: 1, quote_ok: 1, quote_fail: 1, locate_ok: 1, locate_fail: 1, search_no_hits: 1 };
  function gaPayload(body) {
    if (!body || !GA_EVENTS[body.t]) return null;
    var out = { surface: body.pg };
    if (body.src) out.src = body.src;
    if (body.ms != null) out.ms = body.ms;
    if (body.t === 'search_no_hits') { if (body.code != null && isFinite(Number(body.code))) out.len = Number(body.code); }
    else if (body.code != null) out.code = body.code;
    return out;
  }

  var api = {
    gaPayload: gaPayload,
    deliveryAttrValue: deliveryAttrValue, buildEventPayload: buildEventPayload, EVENT_TYPES: EVENT_TYPES,
    dedupeKey: dedupeKey, dedupeCheck: dedupeCheck, quoteFailCode: quoteFailCode, locateFailCode: locateFailCode,
  };
  if (NODE) { module.exports = api; return; }

  // ================================================================================================
  // Browser glue — never throws, never awaits, never blocks the UI.
  // ================================================================================================
  if (window.soTrackingBound) return;
  window.soTrackingBound = true;

  var ENDPOINT = 'https://links.smokeorganic.co.za/delivery/event';
  var history = [];

  // Best-effort page kind (contract B `pg`); a caller can always pass its own.
  function pageKind() {
    try {
      if (document.querySelector('cart-drawer[open], cart-drawer.is-open, cart-drawer.active')) return 'drawer';
      var p = location.pathname;
      if (/^\/cart\/?$/.test(p)) return 'cart';
      if (/^\/products\//.test(p)) return 'pdp';
      if (/^\/collections\//.test(p)) return 'collection';
      if (p === '/') return 'home';
    } catch (e) { /* ignore — falls back to 'other' */ }
    return 'other';
  }

  // GA4 mirror (section F): fire-and-forget, never throws; a no-op when Shopify analytics isn't on the page.
  function publishGa(body) {
    try {
      var data = gaPayload(body);
      if (data && window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('so:' + body.t, data);
      }
    } catch (e) { /* never blocks the server event */ }
  }

  // navigator.sendBeacon(url, text/plain Blob) so it survives page unload without a CORS preflight; a fetch
  // fallback (keepalive, no-cors — the 204 is unreadable either way, we never read the response). Never
  // awaited, never throws.
  function send(type, fields) {
    try {
      fields = fields || {};
      if (!fields.pg) fields.pg = pageKind();
      var d = dedupeCheck(history, type, fields.pc, fields.code, Date.now());
      history = d.history;
      if (!d.allow) return;
      var body = buildEventPayload(type, fields);
      if (!body) return;
      publishGa(body);
      var json = JSON.stringify(body);
      if (json.length > 1024) return;                        // contract: ≤1 KB — drop rather than guess a trim
      if (navigator.sendBeacon) {
        var ok = false;
        try { ok = navigator.sendBeacon(ENDPOINT, new Blob([json], { type: 'text/plain' })); } catch (e) { ok = false; }
        if (ok) return;
      }
      if (typeof fetch === 'function') {
        fetch(ENDPOINT, { method: 'POST', keepalive: true, mode: 'no-cors', headers: { 'Content-Type': 'text/plain' }, body: json })
          .catch(function () { /* the endpoint isn't deployed yet (Task 29): a 404/405 must not surface anywhere */ });
      }
    } catch (e) { /* never throws, never blocks the UI */ }
  }

  // ---- D. Clarity tags ------------------------------------------------------------------------
  // Guarded: no throw if Clarity is absent or blocked. The postcode itself is never passed.
  function clarity() {
    try {
      if (typeof window.clarity !== 'function') return;
      window.clarity.apply(window, arguments);
    } catch (e) { /* Clarity absent/blocked */ }
  }
  function claritySet(hasPc, src) {
    clarity('set', 'so_pc', hasPc ? 'yes' : 'no');
    if (src) clarity('set', 'so_pc_src', src);
  }
  // Closed list (contract D): so_sheet_open | so_locate_tap | so_locate_fail | so_quote_fail | so_postcode_set.
  var CLARITY_EVENTS = { so_sheet_open: 1, so_locate_tap: 1, so_locate_fail: 1, so_quote_fail: 1, so_postcode_set: 1 };
  function clarityEvent(name) { if (CLARITY_EVENTS[name]) clarity('event', name); }

  // ---- the hidden cart attribute writer --------------------------------------------------------
  // Debounced 1 s; POSTs /cart/update.js {attributes:{_so_delivery}} ONLY when it differs from what we
  // already know the cart holds — never an extra /cart.js fetch here (the caller passes the cart JSON it
  // already has, from the cart row's own /cart.js read or a theme cart event). Shopify's /cart/update.js
  // merges `attributes` (it does not replace the object), so this never touches so-preorder-cart's `Preorder`
  // key — only `_so_delivery` is ever sent in the body.
  var lastKnownAttr, writeTimer = null, writing = false, pendingWrite = null;
  function knownAttr(cart) { return cart && cart.attributes && typeof cart.attributes._so_delivery === 'string' ? cart.attributes._so_delivery : undefined; }
  function writeAttr(value) {
    if (writing) { pendingWrite = value; return; }
    writing = true;
    fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _so_delivery: value } }),
    }).then(function () { lastKnownAttr = value; }, function () { /* non-blocking; retried on the next differing sync */ })
      .then(function () {
        writing = false;
        if (pendingWrite != null && pendingWrite !== lastKnownAttr) { var v = pendingWrite; pendingWrite = null; writeAttr(v); }
        else pendingWrite = null;
      });
  }
  // cart = the freshest cart JSON already in hand (may be null/undefined — then we go only off what we've
  // written ourselves this page load); parts = deliveryAttrValue() parts, or {pc: null} to clear.
  function syncDeliveryAttr(cart, parts) {
    try {
      var value = deliveryAttrValue(parts);
      var known = knownAttr(cart);
      if (known !== undefined) lastKnownAttr = known;
      if (lastKnownAttr === value) return;
      clearTimeout(writeTimer);
      writeTimer = setTimeout(function () { writeAttr(value); }, 1000);
    } catch (e) { /* non-blocking */ }
  }

  window.SO = window.SO || {};
  window.SO.tracking = {
    send: send, clarity: clarity, claritySet: claritySet, clarityEvent: clarityEvent,
    syncDeliveryAttr: syncDeliveryAttr, deliveryAttrValue: deliveryAttrValue,
    quoteFailCode: quoteFailCode, locateFailCode: locateFailCode,
  };
})();
